// app-version.ts — tell an app when a newer deploy is live than the one its
// page was loaded from: backlit.checkVersion() and the "app-update-available"
// event (backlit.on).
//
// Two versions are compared, both opaque version ids:
//
//   client — the version this page's HTML was served from. A document cannot
//     read its own response headers, so it comes from (1) the edge's
//     `Server-Timing: backlit-version;desc="<ver>"` on the page's navigation
//     entry, else (2) the platform service worker, which records the
//     X-Backlit-Version of each navigation it serves — including a cached copy
//     served offline (backlit-sw.js "backlit:version-query"). Resolved once per
//     page, at load, while the worker that served the navigation is still
//     alive. null when neither source knows.
//   server — the glow's live version, from the X-Backlit-Version header on a
//     2xx heartbeat (heartbeat.ts). The heartbeat is unmetered, so learning it
//     costs the owner nothing. null when the beat got no answer (offline) or a
//     non-2xx (e.g. a Private session that has ended).
//
// Automatic announcements run ONLY on an offline.json glow (the worker's reply
// says whether it serves the app offline): every 2xx beat — load, hourly,
// →visible, plus one fired when the browser reports it is back online — is
// compared, and each newly seen server version is announced once. An explicit
// checkVersion() announces on any glow whenever the two differ.
//
// An announcement made before the app subscribed is not lost: a handler added
// while a mismatch is outstanding receives it once, asynchronously.

import { assertOnBacklitHost, isBacklitHost } from "./host.js";
import { onHeartbeatResult, sendHeartbeat, sendHeartbeatWithInfo, type HeartbeatInfo } from "./heartbeat.js";
import type { Unsubscribe } from "./backend.js";

// AppVersion is checkVersion()'s answer (mirrored in sdk/sdk.d.ts).
export interface AppVersion {
  readonly client: string | null;
  readonly server: string | null;
}

// AppUpdateAvailableEvent is the "app-update-available" payload: both versions
// are known and differ.
export interface AppUpdateAvailableEvent {
  readonly client: string;
  readonly server: string;
}

export type AppUpdateHandler = (event: AppUpdateAvailableEvent) => void;

// Must-match with cmd/edge: the Server-Timing metric name (assets.go
// versionServerTiming) and the worker's message types (backlit-sw.js).
export const SERVER_TIMING_METRIC = "backlit-version";
export const SW_MSG_VERSION_QUERY = "backlit:version-query";
export const SW_MSG_VERSION = "backlit:version";

// How long the load-time worker query may take before the answer is "unknown".
const SW_ASK_MS = 2000;

interface ClientInfo {
  version: string | null;
  offline: boolean;
}

let started = false;
let client: Promise<ClientInfo> | null = null;
// announced is the server version last announced (null: none outstanding);
// pending is that announcement, replayed to a handler registered later.
let announced: string | null = null;
let pending: AppUpdateAvailableEvent | null = null;
// explicit counts checkVersion() calls waiting on a beat: that beat is theirs to
// announce, so the automatic listener stands aside (no double event).
let explicit = 0;
let askMs = SW_ASK_MS;
const handlers = new Set<AppUpdateHandler>();
let teardown: Array<() => void> = [];

// fromServerTiming reads the version the edge stamped on this page's navigation
// response, or null (no entry, no metric, or no Server-Timing support).
function fromServerTiming(): string | null {
  try {
    const perf = (globalThis as { performance?: Performance }).performance;
    const nav = perf?.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined;
    for (const e of nav?.serverTiming ?? []) {
      if (e.name === SERVER_TIMING_METRIC && typeof e.description === "string" && e.description !== "") {
        return e.description;
      }
    }
  } catch {
    /* no Performance Timeline */
  }
  return null;
}

// askWorker asks the controlling service worker which version it served this
// page, and whether it serves the app offline. null: no controller, no reply in
// time, or no MessageChannel.
function askWorker(): Promise<ClientInfo | null> {
  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let port: MessagePort | null = null;
    const done = (v: ClientInfo | null) => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      try {
        port?.close();
      } catch {
        /* ignore */
      }
      resolve(v);
    };
    try {
      const ctl = (globalThis as { navigator?: Navigator }).navigator?.serviceWorker?.controller;
      if (!ctl || typeof MessageChannel !== "function") {
        done(null);
        return;
      }
      const ch = new MessageChannel();
      port = ch.port1;
      port.onmessage = (ev: MessageEvent) => {
        const d = ev.data as { type?: unknown; version?: unknown; offline?: unknown } | null;
        if (!d || d.type !== SW_MSG_VERSION) return;
        done({
          version: typeof d.version === "string" && d.version !== "" ? d.version : null,
          offline: d.offline === true,
        });
      };
      timer = setTimeout(() => done(null), askMs);
      ctl.postMessage({ type: SW_MSG_VERSION_QUERY }, [ch.port2]);
    } catch {
      done(null);
    }
  });
}

// resolveClient resolves (once per page) the version this page was served from
// and whether the glow is served offline.
function resolveClient(): Promise<ClientInfo> {
  if (!client) {
    client = (async () => {
      const timed = fromServerTiming();
      const sw = await askWorker();
      return { version: timed ?? sw?.version ?? null, offline: sw?.offline === true };
    })();
  }
  return client;
}

// serverOf is the live version a settled beat stated, or null.
function serverOf(status: number, info: HeartbeatInfo): string | null {
  return status >= 200 && status < 300 && info.version !== "" ? info.version : null;
}

// emit delivers ev to every handler in registration order; a thrown handler is
// reported and never stops the rest (the storage.change contract).
function emit(ev: AppUpdateAvailableEvent): void {
  for (const h of Array.from(handlers)) {
    try {
      h(ev);
    } catch (e) {
      console.error("[backlit] app-update-available handler threw", e);
    }
  }
}

function announce(clientVer: string, server: string): void {
  const ev: AppUpdateAvailableEvent = Object.freeze({ client: clientVer, server });
  announced = server;
  pending = ev;
  emit(ev);
}

// noteMatch records a comparison of equal versions, which clears any outstanding
// announcement (a rollback, or the page was reloaded onto the live version).
function noteMatch(): void {
  announced = null;
  pending = null;
}

// onBeat is the automatic path: offline.json glows only, once per new version.
function onBeat(status: number, info: HeartbeatInfo): void {
  if (explicit > 0) return;
  const server = serverOf(status, info);
  if (server === null) return;
  void resolveClient().then((c) => {
    if (!c.offline || c.version === null) return;
    if (server === c.version) noteMatch();
    else if (server !== announced) announce(c.version, server);
  });
}

// checkVersion compares the page's version with the live one and announces a
// difference. server is null when the heartbeat got no answer (offline) or a
// non-2xx; nothing is announced unless both versions are known.
export async function checkVersion(): Promise<AppVersion> {
  assertOnBacklitHost();
  explicit++;
  const beat = sendHeartbeatWithInfo().finally(() => {
    explicit--;
  });
  const [c, b] = await Promise.all([resolveClient(), beat]);
  const server = serverOf(b.status, b.info);
  if (server !== null && c.version !== null) {
    if (server === c.version) noteMatch();
    else announce(c.version, server);
  }
  return Object.freeze({ client: c.version, server });
}

// onAppUpdateAvailable registers h; an outstanding announcement is delivered to
// it once, asynchronously. The unsubscribe is idempotent.
export function onAppUpdateAvailable(h: AppUpdateHandler): Unsubscribe {
  handlers.add(h);
  const replay = pending;
  if (replay) {
    void Promise.resolve().then(() => {
      if (!handlers.has(h) || pending !== replay) return;
      try {
        h(replay);
      } catch (e) {
        console.error("[backlit] app-update-available handler threw", e);
      }
    });
  }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    handlers.delete(h);
  };
}

// initAppVersion runs at SDK load: it resolves the page's version straight away
// (the worker that served the navigation is alive now) and starts the automatic
// path. Same guards as heartbeat.ts — non-browser and off-host are no-ops.
export function initAppVersion(): void {
  if (started) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isBacklitHost()) return;
  started = true;
  void resolveClient();
  teardown.push(onHeartbeatResult(onBeat));
  const onOnline = () => {
    if (document.hidden) return; // the →visible beat covers a hidden tab
    void resolveClient().then((c) => {
      if (c.offline) void sendHeartbeat();
    });
  };
  try {
    window.addEventListener("online", onOnline);
    teardown.push(() => window.removeEventListener("online", onOnline));
  } catch {
    /* no event target */
  }
}

// _resetAppVersionForTest clears every module-level state.
export function _resetAppVersionForTest(): void {
  for (const t of teardown) t();
  teardown = [];
  started = false;
  client = null;
  announced = null;
  pending = null;
  explicit = 0;
  askMs = SW_ASK_MS;
  handlers.clear();
}

// _setWorkerAskTimeoutForTest replaces the worker-query timeout (null: 2 s).
export function _setWorkerAskTimeoutForTest(ms: number | null): void {
  askMs = ms ?? SW_ASK_MS;
}
